<template lang="pug">
.rhpa(
  v-if="product"
  :class="[grouped && 'grouped', 'type-' + product.type, forceMobile && 'force-mobile']"
  ref="rhpaEl"
  data-cnstrc-product-detail
  :data-cnstrc-add-all-to-cart="product.type === 'grouped' ? true : undefined"
  :data-cnstrc-item-id="cnstrcItemId"
  :data-cnstrc-item-name="product.name"
  :data-cnstrc-item-variation-id="cnstrcVariationId"
  :data-cnstrc-item-price="cnstrcItemPrice"
)
  .rhpa-head
    .price-container(v-if="!grouped && buyRequest.productId && !product.hundoPricing")
      .starting-at(v-if="showStartingAt") {{ __('Starting At: ') }}
      trial-price.rhpa-top-price(
        v-if="displayTrialPrice"
        :show-excl-vat="true"
        :buyRequest="buyRequest"
        :showTotalPrice="false"
        theme="rhpa"
      )
      price.rhpa-top-price(
        v-else
        :show-excl-vat="true"
        :buyRequest="buyRequest"
        :showTotalPrice="false"
        theme="rhpa"
      )
    badges.rhpa-badges(:buyRequest="buyRequest" :isCartShipsFreeProduct="isCartShipsFreeProduct")
    slot(name="rhpaTop")
    .short(v-translate v-if="activeProduct.attributes.short_description" v-html="activeProduct.attributes.short_description")
    compatibility-message(:buyRequest="buyRequest" v-if="simple.attributes.compat_group")
    free-trial-callout.trial-styles(v-if="trialProduct" :buyRequest="buyRequest" :trialProduct="trialProduct" :key="trialProduct?.productId")
    affirm-promo(v-if="showAffirmPromo" :price="productPrice" :product="product")
    truemed-widget(v-if="showTruemedWidget")
    slot(v-if="customBadge && showTopCustomBadge" name="rhpaCustomBadge")
      .customRhpaBadge
    .divider(v-if="shouldShowDivider")
  .rhpa-body
    slot(name="rhpaBody")
      rhpa-programming(:product="simple" ref="programmingEl" v-if="showDigitalProgram" @addToCart="$refs.footer.addToCart()")
      rogue-related(v-if="relatedProductIds && !showFlooringRhpa" :product-ids="relatedProductIds")
      rhpa-register(v-if="product.hasRegistration" :buyRequest="buyRequest" :sku="product.sku" :productTitle="product.name")
      rhpa-flooring(v-else-if="showFlooringRhpa" :buyRequest="buyRequest" :sku="product.sku")
      rhpa-body(v-else :buyRequest="buyRequest" :addToCartDisabled="!productPrice")
      .divider(v-if="(product.type !== 'simple' && product.type !== 'virtual' && !product.hasRegistration) || (product.type !== 'simple' && (product.customOptions || []).length)")
      rhpa-hundo(
        v-if="product.hundoPricing"
        :product="product"
        :buy-request="buyRequest"
        :price="productPrice"
      )
    slot(name="rhpaFooter")
      rhpa-footer(
        v-if="!product.hasRegistration"
        :buy-request="buyRequest"
        :disable-digital-wallets="showDigitalProgram"
        :disabled="disableCheckout"
        ref="footer"
        :validate="requiresRecipeId ? createRecipe : validate"
      )
    slot(v-if="customBadge && showBottomCustomBadge" name="rhpaCustomBadge")
      .customRhpaBadge
</template>
<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeMount, onServerPrefetch, ref, toRef, watch, type PropType } from 'vue'
import { getItemSku } from '@rogue/universal'
import { useRoute, useRouter } from 'vue-router'
import RhpaBody from './rhpa-body.vue'
import RhpaFooter from './rhpa-footer.vue'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useProductStore } from '@/js/stores/productStore'
import Badges from '@/js/components/rhpa/badges.vue'
import { validate as validateHelper } from '@/js/helpers/validate-fns'
import { computePriceSync } from '@/js/helpers/price-utils'
import { buildBundleVariationId, getCnstrcItemId, resolvePlpRepresentative } from '@/js/services/cnstrc/util'
import type { BuyRequest, Product } from '@/js/services/RogueTypes'
import { useRogue } from '@/js/services/Rogue'
import { isEnabledCustomProduct } from '@/js/customProducts/registry'
import { useMassStorage } from '@/js/composables/useMassStorage'
import { raiseShortKitPanels } from '@/js/pages/product/rackbuilder/utils/mms-utils/mms-panel-utils'
import Loading from '@/js/components/loading.vue'

const AFFIRM_MIN_ORDER = 250

const TrialPrice = defineAsyncComponent(() => import(/* webpackChunkName: "trial-price" */'@/js/components/trial-price.vue'))
const RhpaProgramming = defineAsyncComponent(() => import(/* webpackChunkName: "programs" */ './programs/rhpa-programming.vue'))
const RhpaFlooring = defineAsyncComponent(() => import(/* webpackChunkName: "flooring" */ './flooring/rhpa-flooring.vue'))
// Spinner rather than a blank panel while the events chunk downloads - this boundary sits under
// the SSR-rendered spinner, so anything else here reads as a flicker
const RhpaRegister = defineAsyncComponent({
  loader           : () => import(/* webpackChunkName: "events" */'./rhpa-register.vue'),
  loadingComponent : Loading,
  delay            : 0,
})
const RhpaHundo = defineAsyncComponent(() => import(/* webpackChunkName: "hundo" */'./rhpa-hundo.vue'))
const AffirmPromo = defineAsyncComponent(() => import(/* webpackChunkName: "affirm" */'./affirm-promo.vue'))
const TruemedWidget = defineAsyncComponent(() => import('./truemed-widget.vue'))
const FreeTrialCallout = defineAsyncComponent(() => import(/* webpackChunkName: "trial" */'./free-trial-callout.vue'))
const CompatibilityMessage = defineAsyncComponent(() => import('@/js/components/compatibility/compatibility-message.vue'))

const rhpaEl = ref<HTMLElement>()
const programmingEl = ref<InstanceType<typeof RhpaProgramming> | null>(null)

const props = defineProps({
  buyRequest: {
    type     : Object as PropType<BuyRequest>,
    required : true,
  },
  forceMobile: {
    type    : Boolean,
    default : false,
  },
  pdpProductId: {
    type     : Number,
    required : false,
  },
  customBadge: {
    type    : Boolean,
    default : false,
  },
  customBadgePosition: {
    type    : String,
    default : 'top',
  },
  isCartShipsFreeProduct: {
    type    : Boolean,
    default : false,
  },
  createRecipe: {
    type     : Function,
    required : false,
  },
})

const emit = defineEmits(['change'])

const productPrice = ref(0)
const loading = ref(false)
const purchaseCount = ref<number | null>(null)
const trialProduct = ref<Product | null>(null)

const customerStore = useCustomerStore()
const productStore = useProductStore()

const rogue = useRogue()
const route = useRoute()
const router = useRouter()
const buyRequestRef = toRef(props, 'buyRequest')
const { is3DStorageProduct } = useMassStorage(buyRequestRef)


const products = computed(() => productStore.products)
const earnedProducts = computed(() => customerStore.earnedProducts)
const loggedIn = computed(() => customerStore.loggedIn)
const product = computed(() => products.value[props.buyRequest.productId] || { attributes: {} })

// Recursively check selected children on buy request for applied free trial
function getTrialProduct(br: BuyRequest, checkQty: boolean = false): Product | null {
  if (checkQty && br.qty === 0) {
    return null
  }

  const brProduct = products.value[br.productId]
  // Products not loaded yet. This method will run again after buy request products are loaded
  if (!brProduct) {
    return null
  }

  if (brProduct.attributes?.is_trial) {
    return brProduct
  }

  if (brProduct.attributes?.trial_product_id) {
    // @ts-ignore
    return products.value[brProduct.attributes?.trial_product_id]
  }

  if (br.children?.length) {
    for (let i = 0; i < br.children.length; i += 1) {
      const child = br.children[i]
      const trialResult = getTrialProduct(child, true)
      if (trialResult) {
        return trialResult
      }
    }
  }

  return null
}

const displayTrialPrice = computed(() => trialProduct.value && product.value.type === 'virtual')

const isPurchaseLimitMet = computed(() => {
  if (!purchaseCount.value) {
    return false
  }

  // @ts-ignore
  return purchaseCount.value >= trialProduct.value?.attributes.purchase_limit
})

const simple = computed(() => {
  let request = props.buyRequest
  while (request.children && request.children.length > 0 && request.children[0]) {
    request = request.children[0]
  }

  return products.value[request.productId]
})

const purchaseLimitSKU = computed(() => {
  const localizedSku = getItemSku(props.buyRequest, rogue, rogue.site.defaultStoreId)

  // @ts-ignore
  return simple.value.attributes?.purchase_limit > 0 ? localizedSku : ''
})

const relatedProductIds = computed(() => {
  const pdpProduct = products.value[props.pdpProductId || product.value.productId]

  // Disable the default relatedProductIds if this roguebuilder page has one defined in Roguebuilder
  if (pdpProduct.roguebuilder?.slots?.aboveRhpa?.length) {
    return null
  }

  return (pdpProduct?.relatedIds?.length || 0) > 0 ? pdpProduct.relatedIds : null
})

const activeProduct = computed(() => {
  const productTemp = product.value

  if (productTemp.type === 'grouped' && productTemp.rhpaDisplayType === 'swatch' && props.buyRequest.children?.[0]?.productId) {
    return products.value[props.buyRequest.children[0].productId]
  }

  return productTemp
})

const showStartingAt = computed(() => (props.pdpProductId ? products.value[props.pdpProductId]?.roguebuilder?.props?.showStartingAt : false))

const grouped = computed(() => (product.value.type === 'grouped' || product.value.hasRegistration) && product.value.rhpaDisplayType !== 'swatch')

const hasEarnedProduct = computed(() => earnedProducts.value?.[product.value.productId] || (trialProduct.value && earnedProducts.value?.[trialProduct.value.productId]))

const disableCheckout = computed(() => !trialProduct.value && !!(isPurchaseLimitMet.value
  || (!productPrice.value && (!hasEarnedProduct.value || props.buyRequest.qty === 0))))

const showDigitalProgram = computed(() => simple.value.isProgram)
const showFlooringRhpa = computed(() => product.value.sku === 'RG000_NEW' || product.value.sku === 'RG0077-Group' || product.value.sku === 'RG0065-Group' || product.value.sku === 'RGAKTIVPRO-GROUP')
const showAffirmPromo = computed(() => (rogue.site.code === 'USA' && productPrice.value > AFFIRM_MIN_ORDER))
const showTruemedWidget = computed<boolean>(() => rogue.featureFlags.isOn('truemed_enabled_spa') && !!simple.value.attributes.show_truemed_promo)
const showTopCustomBadge = computed(() => props.customBadge && props.customBadgePosition === 'top')
const showBottomCustomBadge = computed(() => props.customBadge && props.customBadgePosition === 'bottom')

const isTheCave = computed(() => isEnabledCustomProduct(rogue, 'cave', product.value))
const groupedProduct = computed(() => {
  const groupedId = props.buyRequest.metadata?.groupedProductId

  return groupedId ? products.value[groupedId] : undefined
})
const isSlingerX = computed(() => isEnabledCustomProduct(rogue, 'slinger-x', product.value, groupedProduct.value))
const isHybridRig = computed(() => isEnabledCustomProduct(rogue, 'hybrid-rig', product.value))
const requiresRecipeId = computed(() => isTheCave.value || is3DStorageProduct.value || isSlingerX.value || isHybridRig.value)

const shouldShowDivider = computed(() => {
  if (is3DStorageProduct.value || isTheCave.value || isSlingerX.value || isHybridRig.value) {
    return false
  }

  return !showTopCustomBadge.value && !trialProduct.value
})

// For grouped products that contain a bundle child — find that child's buy request.
const bundleChildBuyRequest = computed(() =>
  props.buyRequest.children?.find(c => products.value[c.productId]?.type === 'bundle') ?? null
)

const cnstrcItemId = computed(() => getCnstrcItemId(props.buyRequest, products.value))

const cnstrcVariationId = computed(() => {

  // Pure grouped: no variation-id (and must come before the grouped-with-bundle branch).
  if (product.value.type === 'grouped' && !bundleChildBuyRequest.value) return undefined

  if (product.value.type === 'grouped' && bundleChildBuyRequest.value) {
    if (!simple.value) return undefined
    const bundleProduct = products.value[bundleChildBuyRequest.value.productId] as any
    
    return buildBundleVariationId(simple.value, bundleChildBuyRequest.value, bundleProduct, route.query.b as string | undefined, products.value, rogue)
  }

  if (product.value.type === 'bundle') {
    if (!simple.value || simple.value.productId === product.value.productId) return undefined
    
    return buildBundleVariationId(simple.value, props.buyRequest, product.value as any, route.query.b as string | undefined, products.value, rogue)
  }

  if (!simple.value || simple.value.productId === product.value.productId) return undefined

  // Configurable: stable per-color representative so variation-id doesn't drift across sizes.
  const rep = resolvePlpRepresentative(product.value, simple.value, products.value)
  
  return rep ? rep.productId : simple.value.productId
})

const cnstrcItemPrice = computed(() => {
  if (props.buyRequest.loading) return undefined

  try {
    return computePriceSync({ ...props.buyRequest, qty: 1 }, rogue.pinia!) || undefined
  } catch {
    return undefined
  }
})

async function checkPurchaseLimitCount() {
  await customerStore.init(rogue)

  // don't make API call for trial products as the trial callout has it's own API call
  if (!loggedIn.value || !purchaseLimitSKU.value || trialProduct.value) {
    return
  }

  try {
    const data = await rogue.fetch<any>('/svc_checkout/order/history', 'post', { items: [purchaseLimitSKU.value] })

    purchaseCount.value = data[purchaseLimitSKU.value]?.count || 0
  } catch (error: any) {
    rogue.log.error('Error loading order history', error)
  }
}

function updatePrice() {
  if (props.buyRequest.loading) {
    return
  }

  const toCheck: BuyRequest = Object.assign({}, props.buyRequest, { qty: 1 })

  if (is3DStorageProduct.value) {
    toCheck.children = JSON.parse(JSON.stringify(props.buyRequest.children ?? []))
    raiseShortKitPanels(toCheck, rogue)
  }

  try {
    const basePrice = computePriceSync(toCheck, rogue.pinia!)
    productPrice.value = basePrice * props.buyRequest.qty
  } catch (e: any) {
    if (!props.buyRequest.isGiftcard) {
      productPrice.value = 0
    }
    
    const errorData = {
      buyRequest : props.buyRequest,
      product    : product.value,
      url        : route,
      siteId     : rogue.site.id,
    }

    rogue.log.error('Error computing price in RHPA', errorData)

    if (route?.path) {
      router.push(route.path) // redirect to the default PDP if pricing breaks
    }
  }
}

function getBuyRequestProductIds(br: BuyRequest): number[] {
  if (!br.children?.length) {
    return [br.productId]
  }

  return br.children.map(child => getBuyRequestProductIds(child)).flat().concat([br.productId])
}

async function loadAllBuyRequestProducts() {
  const productIds = getBuyRequestProductIds(props.buyRequest)
  await productStore.loadAll(productIds, rogue)

  const trialProductIds = productIds
    .map(id => products.value[id])
    .filter(p => p?.attributes?.trial_product_id)
    .map(p => p?.attributes.trial_product_id as string)
  await productStore.loadAll(trialProductIds, rogue)
}

async function load() {
  updatePrice()
  await loadAllBuyRequestProducts()
  trialProduct.value = getTrialProduct(props.buyRequest)
}

async function validate() {
  if (purchaseLimitSKU.value.length > 0) {
    await checkPurchaseLimitCount()

    if (isPurchaseLimitMet.value) {
      throw new Error('Product not added to cart because the purchase limit has been met')
    }
  }

  // Check if this is an ebook / program
  if (simple.value.isProgram) {
    // If we haven't checked whether the customer is already subscribed, we dont want to allow A2C
    if (!programmingEl.value) {
      return false
    }

    return programmingEl.value.validate()
  }

  if (product.value.type === 'giftcard' && product.value.giftcardData?.type?.toLowerCase() === 'virtual' && rhpaEl.value) {
    return validateHelper(rhpaEl.value)
  }

  return true
}

watch(() => props.buyRequest, () => {
  if (!loading.value) {
    try {
      trialProduct.value = getTrialProduct(props.buyRequest)
      emit('change')
      updatePrice()
    } catch (e: any) {
      if (product.value.type !== 'giftcard') {
        rogue.log.warn('Error getting price on RHPA', { buyRequest: props.buyRequest, productName: product.value.name, error: e.message })
      }
    }
  }
}, {
  deep: true,
})

watch(loggedIn, () => {
  if (!loggedIn.value) {
    purchaseCount.value = null
  }
}, {
  deep: true,
})

onBeforeMount(() => {
  trialProduct.value = getTrialProduct(props.buyRequest)
  load()
  checkPurchaseLimitCount()

  // When a grouped product page is loaded with a ?sku= URL param, createBuyRequest sets
  // that child's qty to 1 regardless of stock status. If the pre-selected child is OOS or BIN,
  // zero it out here so it doesn't sneak into the cart when the user picks a different variant.
  // Dropship (DROPSHIP_UPS) children are excluded because their availability is managed externally.
  if (product.value?.type === 'grouped') {
    props.buyRequest.children?.forEach(child => {
      const childProduct = products.value[child.productId]
      const isDropship   = childProduct?.attributes?.shipperhq_shipping_group?.includes('DROPSHIP_UPS')

      let isUnavailable: boolean
      if (childProduct?.type === 'configurable' && child.children?.length === 1) {
        // For a configurable grouped child, availability lives on the selected simple variant (grandchild),
        // not on the configurable itself whose inventory reflects aggregate stock.
        const grandchild = products.value[child.children[0].productId]
        isUnavailable = !grandchild || grandchild.inventory?.max === 0 || !!grandchild.inventory?.bin
      } else {
        isUnavailable = childProduct?.inventory?.max === 0 || !!childProduct?.inventory?.bin
      }

      if (isUnavailable && !isDropship) {
        child.qty = 0
      }
    })
  }
})

onServerPrefetch(async () => {
  await load()
})
</script>

<style lang="scss">
.product-virtual .registry.link {
  display: none;
}
</style>

<style lang="scss" scoped>
.grouped {

  &.oos,
  &.bin {

    .rhpa-oos,
    .rhpa-bin {
      display: none;
    }
  }
}

.check-giftcard-balance {
  margin-top: $space-m;
}

.rhpa-head .short {
  margin-top: $space-s;
  font-size: $size-xxs;

  :deep(p) {
    margin-bottom: 0;
  }
}

.rhpa-badges {
  margin: $space-xxs 0;
}

.rhpa-apple-pay-button {
  margin-top: $space-xxs;
}

.divider {
  height: 1px;
  background-color: $color-gray;
  margin: $size-s 0;
}

.trial-styles {
  margin: $size-s 0;
}

.rhpa-head .divider {
  display: none;
}

.badges {
  margin-top: $space-xs;
}

.rhpa-load {
  display: flex;
  justify-content: center;
  height: 200px;
  align-items: center;
}

.rhpa:not(.force-mobile) {
  @include not-mobile() {
    .rhpa-head .divider {
      display: block;
    }

    .check-giftcard-balance {
      display: none;
    }
  }

  @include all-desktop() {
    .rhpa-badges {
      margin: $space-xxs 0 0;
    }
  }
}

.price-container {
  display: flex;
  align-items: center;

  .starting-at {
    font-style: italic;
    margin-right: $space-xxs;
  }

  .rhpa-top-price.product-price.rhpa {
    margin-bottom: 0;
  }
}
</style>

<style lang="scss" scoped target="app">
.platform-ios,
.platform-android {
  #BVRRSummaryContainer:deep() {

    .bv-write-review-container,
    .bv-write-container {
      display: none !important;
    }
  }
}
</style>
